'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { TDynamicData } from '../../types/dynamic-data';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Reveal } from '../ui/reveal';

interface ProcessCardProps {
  process: TDynamicData;
  index: number;
}

export function Process({
  process,
  subtitle,
  title,
}: {
  process: TDynamicData[];
  subtitle: string;
  title: string;
}) {
  return (
    <section
      className={cn(
        'custom-section',
        'pb-16 pt-8 sm:pb-20 sm:pt-12 md:pb-28 md:pt-16 lg:pb-36 lg:pt-[72px]',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3 xl:gap-6',
        )}
      >
        <Reveal variant="left">
          <Heading
            subtitle={subtitle}
            title={title}
            className={{
              container: 'mb-4 w-full max-w-[300px] sm:mb-0',
              title: 'text-balance',
            }}
          />
        </Reveal>

        {process.map((item, index) => (
          <ProcessCard key={index} process={item} index={index} />
        ))}
      </div>
    </section>
  );
}

export function ProcessCard({ process, index }: ProcessCardProps) {
  const defaultImage = process?.fields?.[0]?.images?.[0]?.url ?? '/images/placeholder.png';
  const hoverImage = process?.fields?.[0]?.images?.[1]?.url ?? defaultImage;

  return (
    <Reveal variant="bottom">
      <div
        className={cn(
          'col-span-1 flex h-fit w-full flex-col items-start justify-between gap-8 border p-4 sm:h-[210px] sm:gap-0 md:p-5 lg:h-[254px] xl:p-6',
          'group border-black transition-all duration-300 ease-in-out hover:bg-black',
          'ea-process-card',
        )}
      >
        <div className="relative flex w-full items-start justify-between">
          <span
            className={cn(
              'cursor-context-menu text-lg font-bold tracking-tighter text-primary sm:text-xl lg:text-2xl',
              'transition-all group-hover:text-primary-foreground',
            )}
          >
            0{index + 1}
          </span>
          <div className="relative h-11 w-11 sm:h-12 sm:w-12 xl:h-14 xl:w-14">
            <Image
              src={defaultImage}
              alt={resolveImageAlt(process?.fields?.[0]?.images?.[0], `${(process?.name ?? "")} default`)}
              fill
              sizes="(max-width: 640px) 44px, (max-width: 1024px) 48px, 56px"
              className={cn(
                'object-cover transition-opacity duration-300',
                'group-hover:opacity-0',
              )}
              loading="lazy"
              fetchPriority="low"
            />
            <Image
              src={hoverImage}
              alt={resolveImageAlt((process?.fields?.[0]?.images?.[1]?.url != null ? process?.fields?.[0]?.images?.[1] : process?.fields?.[0]?.images?.[0]), `${(process?.name ?? "")} hover`)}
              fill
              sizes="(max-width: 640px) 44px, (max-width: 1024px) 48px, 56px"
              className={cn(
                'object-cover opacity-0 transition-opacity duration-300',
                'group-hover:opacity-100',
              )}
              loading="lazy"
              fetchPriority="low"
            />
          </div>
        </div>

        <div className="flex w-full flex-col items-start justify-start gap-2">
          <h3
            className={cn(
              'cursor-context-menu text-lg font-bold text-black sm:text-xl xl:text-2xl',
              'transition-all group-hover:text-white',
            )}
          >
            {process.name}
          </h3>
          <p
            className={cn(
              'cursor-context-menu text-sm text-muted-foreground md:text-base',
              'transition-all group-hover:text-white',
            )}
          >
            {process?.fields?.[1]?.value}
          </p>
        </div>
      </div>
    </Reveal>
  );
}
